import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, Ticket } from "lucide-react";
import { api } from "../../lib/api";
import { logger } from "../../lib/logger";
import { AddToCalendar } from "./AddToCalendar";

const fmtRange = (a, b) => {
  const o = { day: "numeric", month: "short" };
  return `${new Date(a).toLocaleDateString("fr-FR", o)} — ${new Date(b).toLocaleDateString("fr-FR", { ...o, year: "numeric" })}`;
};

const shiftWeek = (iso, delta) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().slice(0, 10);
};

const Session = ({ s, date, season, isNext, onTrial }) => (
  <li className={`rounded-lg border px-3 py-2 text-xs ${s.past ? "opacity-50 border-[color:var(--border)]" : "border-[color:var(--border)] bg-[color:var(--bg)]"} ${isNext ? "ring-1 ring-[color:var(--accent)]" : ""}`} data-testid="planning-session">
    <div className="flex items-center justify-between gap-2">
      <span className="font-mono tabular-nums text-[color:var(--text)] whitespace-nowrap">{s.start}–{s.end}</span>
      {!s.past && <AddToCalendar session={s} date={date} season={season} />}
    </div>
    <div className="flex items-center gap-1.5 mt-1">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.color }} />
      <span className="font-medium truncate">{s.course_name}</span>
      {isNext && <span className="ml-auto shrink-0 text-[9px] uppercase tracking-widest text-[color:var(--accent)]">Prochaine</span>}
    </div>
    <div className="text-[10px] text-[color:var(--text-muted)] truncate">{s.level}{s.age ? ` (${s.age})` : ""} · {s.room}</div>
    {!s.past && onTrial && (season?.first_class_free ?? true) && (
      <button type="button" onClick={() => onTrial(s, date)} data-testid={`trial-btn-${s.course_id}-${s.start}`} className="mt-1.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[color:var(--accent)] hover:underline"><Ticket className="w-3 h-3" /> Essai gratuit</button>
    )}
  </li>
);

export const WeekPlanning = ({ season, onTrial }) => {
  const [week, setWeek] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/courses/planning", { params: week ? { week } : {} })
      .then(r => setData(r.data))
      .catch(err => logger.warn("[courses] planning failed:", err));
  }, [week]);

  if (!data) return <div className="glass rounded-2xl p-10 text-center text-sm text-[color:var(--text-muted)]" data-testid="planning-loading">Chargement du planning…</div>;
  const next = data.next_session;
  const isNext = (day, s) => next && next.date === day.date && next.course_id === s.course_id && next.slot?.start === s.start;

  return (
    <div className="glass rounded-2xl p-5 sm:p-6" data-testid="week-planning">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <CalendarDays className="w-4 h-4 text-[color:var(--accent)]" />
        <h3 className="font-display text-2xl">Cette semaine</h3>
        <span className="text-xs text-[color:var(--text-muted)]" data-testid="planning-range">{fmtRange(data.week_start, data.week_end)}</span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={() => setWeek(shiftWeek(data.week_start, -1))} data-testid="planning-prev" aria-label="Semaine précédente" className="w-8 h-8 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] grid place-items-center transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" onClick={() => setWeek(null)} data-testid="planning-today" className="px-3 h-8 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] text-[10px] uppercase tracking-widest transition-colors">Aujourd'hui</button>
          <button type="button" onClick={() => setWeek(shiftWeek(data.week_start, 1))} data-testid="planning-next" aria-label="Semaine suivante" className="w-8 h-8 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] grid place-items-center transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {data.days.map(day => (
          <div key={day.date} className={`rounded-xl border p-2.5 min-h-[96px] ${day.is_today ? "border-[color:var(--accent)]/60 bg-[color:var(--accent)]/5" : "border-[color:var(--border)]"}`} data-testid={`planning-day-${day.date}`}>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{day.label.slice(0, 3)}</span>
              <span className={`font-display text-2xl leading-none ${day.is_today ? "text-[color:var(--accent)]" : ""}`}>{day.day_num}</span>
            </div>
            <ul className="space-y-1.5">
              {day.sessions.map((s, i) => <Session key={`${s.course_id}-${i}`} s={s} date={day.date} season={season} isNext={isNext(day, s)} onTrial={onTrial} />)}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[color:var(--text-muted)] mt-4">Jours fériés et vacances définis par la régie = pas de cours. Icône agenda sur une séance → ajout Google / iCal (séance unique ou toute la saison).</p>
    </div>
  );
};
